'use client'

import type React from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'

import {
  DEFAULT_TENANT,
  TENANT_STORAGE_KEY,
  type TenantConfig,
  type TenantId,
  appendTenantToPath,
  AXA_PREVENTION_ALIAS_TENANT_ID,
  AXA_PREVENTION_CANONICAL_TENANT_ID,
  getTenantConfig,
  normalizeTenant,
  resolveTenantId,
  syncTenantCookie,
  tenantFromHostname,
  tenantShellClassNames,
  tenantShouldOmitQueryParam,
} from '../utils/tenant'

function appendPreviewQueryFromSearch(path: string, searchParams: URLSearchParams): string {
  const previewKeys = ['axa_preview'] as const
  let result = path
  for (const key of previewKeys) {
    const value = searchParams.get(key)
    if (!value) continue
    const hashIndex = result.indexOf('#')
    const pathWithoutHash = hashIndex >= 0 ? result.slice(0, hashIndex) : result
    const hash = hashIndex >= 0 ? result.slice(hashIndex) : ''
    const [pathname, query = ''] = pathWithoutHash.split('?')
    const params = new URLSearchParams(query)
    params.set(key, value)
    result = `${pathname}?${params.toString()}${hash}`
  }
  return result
}

type TenantContextValue = {
  appendTenantToPath: (path: string) => string
  tenantConfig: TenantConfig
  tenantId: TenantId
}

const TenantContext = createContext<TenantContextValue | null>(null)

export function TenantProvider({
  children,
  defaultTenant = DEFAULT_TENANT,
  layout = true,
}: {
  children: React.ReactNode
  /** Fallback when URL / env / sessionStorage do not resolve a tenant. */
  defaultTenant?: TenantId
  /** When false, only provides context (no outer `min-h-dvh` shell). Use inside AXA v2 scroll. */
  layout?: boolean
}) {
  const pathname = usePathname()
  const [tenantId, setTenantId] = useState<TenantId>(() => {
    if (typeof window === 'undefined') return defaultTenant
    return resolveTenantId(defaultTenant)
  })

  useEffect(() => {
    const fromQuery = normalizeTenant(
      new URLSearchParams(window.location.search).get('tenant'),
    )
    if (fromQuery) {
      sessionStorage.setItem(TENANT_STORAGE_KEY, fromQuery)
      setTenantId(fromQuery)
      return
    }
    const fromHost = tenantFromHostname()
    if (fromHost) {
      sessionStorage.setItem(TENANT_STORAGE_KEY, fromHost)
      setTenantId(fromHost)
      return
    }
    if (defaultTenant !== DEFAULT_TENANT) {
      sessionStorage.setItem(TENANT_STORAGE_KEY, defaultTenant)
      setTenantId(defaultTenant)
      return
    }
    const stored = normalizeTenant(sessionStorage.getItem(TENANT_STORAGE_KEY))
    if (stored) {
      setTenantId(stored)
      return
    }
    setTenantId(resolveTenantId(defaultTenant))
  }, [defaultTenant, pathname])

  useEffect(() => {
    syncTenantCookie(tenantId)
  }, [tenantId])

  /** Canonicalize legacy `?tenant=axa-prevention-v2` → `axa-prevention`. */
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const rawTenant = params.get('tenant')?.trim().toLowerCase()
    if (rawTenant !== AXA_PREVENTION_ALIAS_TENANT_ID) return
    params.set('tenant', AXA_PREVENTION_CANONICAL_TENANT_ID)
    const query = params.toString()
    const next = `${pathname}?${query}${window.location.hash}`
    window.history.replaceState(null, '', next)
  }, [pathname])

  /** Drop redundant `?tenant=` when the hostname already selects the tenant. */
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const fromQuery = normalizeTenant(params.get('tenant'))
    if (!fromQuery || !tenantShouldOmitQueryParam(fromQuery, window.location.hostname)) return
    if (!params.has('tenant')) return
    params.delete('tenant')
    const query = params.toString()
    const next = `${pathname}${query ? `?${query}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', next)
  }, [pathname])

  const tenantConfig = useMemo(() => getTenantConfig(tenantId), [tenantId])

  const value = useMemo<TenantContextValue>(
    () => ({
      appendTenantToPath: (path: string) => {
        const previewParams =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams()
        return appendPreviewQueryFromSearch(appendTenantToPath(path, tenantId), previewParams)
      },
      tenantConfig,
      tenantId,
    }),
    [tenantConfig, tenantId]
  )

  return (
    <TenantContext.Provider value={value}>
      {layout ? (
        <div
          className={`min-h-dvh w-full ${tenantShellClassNames(tenantId)} ${tenantConfig.appShellClassName}`}
          style={tenantConfig.themeStyle as React.CSSProperties}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </TenantContext.Provider>
  )
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider')
  }
  return context
}

export function useTenantOptional(): TenantContextValue | null {
  return useContext(TenantContext)
}
